/**
 * iter87.189 — Cancel Pro+ confirmation modal.
 *
 * Two-step contract with the backend `/api/rivalry-pro/subscription/cancel`:
 *   Step 1 — User types the literal string "CANCEL" (case-sensitive).
 *            Submit stays disabled until it matches.
 *   Step 2 — On success, backend returns `access_until` (ISO date).
 *            Modal switches to a confirmation panel showing the exact
 *            date the user retains Pro+ access until.
 *
 * Copy tone locked with operator on 2026-02-10: matter-of-fact,
 * mirrors the tagline voice ("Every crew you run or play in — one
 * place"). No dark patterns, no artificial urgency, no upsell.
 */
import { useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { X, AlertTriangle, Check } from "lucide-react";
import { proApi } from "./api";

export default function CancelProPlusModal({ open, onClose, onCancelled }) {
    const [confirmText, setConfirmText] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null); // { access_until, message }

    if (!open) return null;

    const canSubmit = confirmText === "CANCEL" && !submitting;

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            const r = await proApi.cancelSubscription();
            // iter87.204 — Refresh the parent's `me` snapshot right away so
            // `latest_subscription.cancel_pending` is true by the time this
            // modal closes. Without this, the Clerk Billing tab kept showing
            // the STALE pre-cancel `me` fetched at page load, so the
            // self-serve "Un-cancel subscription" button never appeared —
            // the only way back in looked like emailing support. Awaited
            // (not fire-and-forget) so the success panel never shows before
            // the parent's state actually reflects the cancellation.
            if (onCancelled) {
                await onCancelled();
            }
            setResult(r);
        } catch (err) {
            const msg = err?.response?.data?.detail || err?.message || "Cancellation failed";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        setConfirmText("");
        setResult(null);
        onClose?.();
    };

    // Format the access_until ISO date as a friendly human string.
    const formatDate = (iso) => {
        if (!iso) return "the end of your billing cycle";
        try {
            return new Date(iso).toLocaleDateString(undefined, {
                weekday: "long", year: "numeric", month: "long", day: "numeric",
            });
        } catch {
            return iso;
        }
    };

    return createPortal(
        <div
            className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4"
            onClick={(e) => e.target === e.currentTarget && handleClose()}
            data-testid="cancel-pro-plus-modal"
        >
            <div className="brutal-card w-full max-w-md p-5 space-y-4">
                {result ? (
                    // ---------- Post-cancel confirmation panel ----------
                    <>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 flex items-center justify-center bg-cyber/10 border border-cyber/40 rounded">
                                <Check className="w-5 h-5 text-cyber" />
                            </div>
                            <h3
                                className="font-display font-black uppercase text-base"
                                data-testid="cancel-pro-plus-success-title"
                            >
                                Cancellation confirmed
                            </h3>
                        </div>
                        <p className="text-sm text-zinc-300 leading-relaxed">
                            You'll keep full Pro+ access until <strong className="text-cyber">{formatDate(result.access_until)}</strong>.
                            After that date, your rivalries stay in the system — but you'll need to
                            request Pro+ again to access them.
                        </p>
                        <p className="text-xs text-zinc-500">
                            Changed your mind before that date? Close this, open your account menu
                            → <span className="text-zinc-300">Billing</span>, and hit
                            <span className="text-zinc-300"> Un-cancel subscription</span> — no email needed.
                        </p>
                        <div className="flex justify-end pt-2 border-t border-zinc-800">
                            <button
                                type="button"
                                onClick={handleClose}
                                data-testid="cancel-pro-plus-close"
                                className="brutal-btn text-sm py-1.5"
                            >
                                Got it
                            </button>
                        </div>
                    </>
                ) : (
                    // ---------- Confirmation entry panel ----------
                    <>
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 flex items-center justify-center bg-amber-500/10 border border-amber-500/40 rounded flex-shrink-0">
                                <AlertTriangle className="w-5 h-5 text-amber-400" />
                            </div>
                            <div className="flex-1">
                                <h3
                                    className="font-display font-black uppercase text-base"
                                    data-testid="cancel-pro-plus-title"
                                >
                                    Cancel Rivalry Pro+
                                </h3>
                                <p className="text-xs text-zinc-500 mt-1">
                                    Are you sure? You'll keep access until your current billing cycle ends.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={handleClose}
                                data-testid="cancel-pro-plus-x"
                                aria-label="Close"
                                className="text-zinc-400 hover:text-white transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="text-sm text-zinc-300 space-y-2 border-t border-zinc-800 pt-3">
                            <p>What happens next:</p>
                            <ul className="list-disc list-inside space-y-1 text-xs text-zinc-400">
                                <li>You keep full Pro+ access through the end of your billing cycle.</li>
                                <li>After that date, your rivalries stay in the system but you drop back to free.</li>
                                <li>To use Pro+ again, request access — you'll land in the approval queue like a new signup.</li>
                            </ul>
                        </div>
                        <div>
                            <label
                                htmlFor="cancel-confirm-input"
                                className="text-[11px] uppercase tracking-wider text-zinc-400 font-black block mb-1.5"
                            >
                                Type <span className="text-amber-400 font-mono">CANCEL</span> to confirm
                            </label>
                            <input
                                id="cancel-confirm-input"
                                type="text"
                                value={confirmText}
                                onChange={(e) => setConfirmText(e.target.value)}
                                data-testid="cancel-pro-plus-input"
                                autoComplete="off"
                                autoCapitalize="characters"
                                spellCheck={false}
                                placeholder="CANCEL"
                                className="brutal-input w-full text-sm py-2"
                            />
                        </div>
                        <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-800">
                            <button
                                type="button"
                                onClick={handleClose}
                                data-testid="cancel-pro-plus-abort"
                                className="brutal-btn-ghost text-sm py-1.5"
                            >
                                Never mind
                            </button>
                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={!canSubmit}
                                data-testid="cancel-pro-plus-submit"
                                className="brutal-btn text-sm py-1.5 disabled:opacity-40 disabled:cursor-not-allowed bg-amber-500/10 border-amber-500/60 text-amber-300 hover:bg-amber-500/20"
                            >
                                {submitting ? "Cancelling…" : "Cancel Pro+"}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>,
        document.body,
    );
}
